'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
} from '@rr/ui'
import { useCompanyDepartments } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function DepartmentsList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyDepartments(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by name…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load departments.'
          : 'No departments in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium text-foreground">
                  {d.name ?? '—'}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {d.description ?? '—'}
                </TableCell>
                <TableCell>
                  <StatusChip status={(d.status ?? 'active') as any} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(d.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
